"use client";

import { useState } from "react";
import { NOTE_QUIZ } from "@/lib/note/copy";
import type { NoteQuiz } from "@/lib/note/format";

/**
 * 노트 본문 예시 문제 카드 (:::quiz · src/lib/note/format.ts::parseQuizBlock).
 * 제목 + 라벨(작은 회색) · 지문(있으면 회색 박스) · 문제 · 보기 4개 버튼 (높이 48px 이상).
 * 보기를 누르거나 "정답 보기" 를 누르기 전에는 정답이 보이지 않는다. 누르면 정답·오답 표시와 해설.
 */
export default function NoteQuizCard({ quiz }: { quiz: NoteQuiz }) {
  const [picked, setPicked] = useState<NoteQuiz["answer"] | null>(null);
  const [revealed, setRevealed] = useState(false);
  const done = revealed || picked !== null;

  return (
    <section
      className="mt-6 rounded-[14px] border border-[#E5E7EB] bg-white p-4 lg:p-5"
      style={{ whiteSpace: "normal" }}
      data-testid="note-quiz"
    >
      {quiz.title && (
        <h3 className="text-[17px] font-extrabold text-[color:var(--color-ink)]" data-testid="note-quiz-title">
          {quiz.title}
        </h3>
      )}
      {quiz.label && <div className="mt-0.5 text-[12px] text-[#888]">{quiz.label}</div>}
      {quiz.passage && (
        <div className="mt-3 rounded-[10px] px-3.5 py-3 text-[14px] leading-[1.6] text-[color:var(--color-body)]" style={{ background: "#F5F5F7", whiteSpace: "pre-line" }} data-testid="note-quiz-passage">
          {quiz.passage}
        </div>
      )}
      <p className="mt-3 text-[16px] font-semibold leading-[1.5] text-[color:var(--color-ink)]" style={{ whiteSpace: "pre-line" }}>
        {quiz.q}
      </p>
      <ul className="mt-3 space-y-2">
        {quiz.choices.map((c) => {
          const isAnswer = c.key === quiz.answer;
          const isPicked = c.key === picked;
          const state = !done ? "idle" : isAnswer ? "correct" : isPicked ? "wrong" : "rest";
          const cls = {
            idle: "border-[#E5E7EB] bg-white hover:border-[#9CA3AF]",
            correct: "border-[#16A34A] bg-[#F0FDF4]",
            wrong: "border-[#DC2626] bg-[#FEF2F2]",
            rest: "border-[#E5E7EB] bg-white opacity-70",
          }[state];
          return (
            <li key={c.key}>
              <button
                type="button"
                disabled={done}
                onClick={() => setPicked(c.key)}
                className={`w-full min-h-[48px] flex items-start gap-2.5 rounded-[10px] border px-3.5 py-3 text-left text-[15px] leading-[1.45] text-[color:var(--color-ink)] transition-colors ${cls}`}
                style={{ whiteSpace: "normal" }}
                data-testid="note-quiz-choice"
                data-state={state}
              >
                <span className="shrink-0 font-extrabold" style={{ fontFamily: "var(--font-en)" }}>{c.key}</span>
                <span className="min-w-0 flex-1 break-keep">{c.text}</span>
                {state === "correct" && <span className="shrink-0 text-[12px] font-bold text-[#16A34A]">{NOTE_QUIZ.correct}</span>}
                {state === "wrong" && <span className="shrink-0 text-[12px] font-bold text-[#DC2626]">{NOTE_QUIZ.wrong}</span>}
              </button>
            </li>
          );
        })}
      </ul>
      {!done ? (
        <div className="mt-3 text-right">
          <button
            type="button"
            onClick={() => setRevealed(true)}
            className="min-h-[44px] rounded-[10px] px-4 text-[14px] font-bold text-[color:var(--color-red-text)] hover:underline"
            data-testid="note-quiz-show"
          >
            {NOTE_QUIZ.showAnswer}
          </button>
        </div>
      ) : (
        <div className="mt-4 rounded-[10px] px-3.5 py-3" style={{ background: "#F5F5F7" }} data-testid="note-quiz-why">
          <div className="text-[14px] font-extrabold text-[color:var(--color-ink)]" data-testid="note-quiz-result">
            {picked ? `${picked === quiz.answer ? NOTE_QUIZ.correct : NOTE_QUIZ.wrong} · ` : ""}
            {NOTE_QUIZ.answerIs(quiz.answer)}
          </div>
          {quiz.why && (
            <p className="mt-1.5 text-[14px] leading-[1.6] text-[color:var(--color-body)]" style={{ whiteSpace: "pre-line" }}>
              <span className="font-bold">{NOTE_QUIZ.why}</span> {quiz.why}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
